import { useState, useEffect, useRef } from 'react';
import ReactGA from 'react-ga4';
import SEO from '../components/SEO';
import ApexCharts from 'apexcharts';
import '../styles/market-price.css';

function MarketPrice() {
  const [tableData, setTableData] = useState([]);
  const [period, setPeriod] = useState('3month');
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [mode, setMode] = useState('gold');
  const [goldType, setGoldType] = useState('24k');
  const [rawData, setRawData] = useState([]);
  const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
  const [displayOption, setDisplayOption] = useState('buy');
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    if (rawData.length) {
      processData(rawData);
    }
  }, [mode, goldType]);

  const renderCell = (price, prevPrice) => {
    if (!prevPrice) return <div>{price?.toLocaleString()}</div>;
    const change = price - prevPrice;
    const percent = ((change / prevPrice) * 100).toFixed(2);
    return (
      <>
        <div>{price?.toLocaleString()}</div>
        <div className={`change-text ${change === 0 ? 'neutral' : change > 0 ? 'positive' : 'negative'}`}>
          {change === 0 ? '0.00%' : `${change > 0 ? '▲' : '▼'} ${Math.abs(percent)}%`}
        </div>
      </>
    );
  };

  // 금/은 탭 변경 로깅
  useEffect(() => {
    ReactGA.event({
      category: 'Market Price',
      action: 'Change Metal Type',
      label: mode === 'gold' ? '금' : '은'
    });
  }, [mode]);

  // 기간 변경 로깅
  useEffect(() => {
    const periodLabels = {
      'month': '1개월',
      '3month': '3개월',
      '1year': '1년',
      '3year': '3년',
      '5year': '5년',
      'all': '전체'
    };
    ReactGA.event({
      category: 'Market Price',
      action: 'Change Period',
      label: periodLabels[period] || period
    });
  }, [period]);

  const handleCustomDateSearch = () => {
    if (startDate && endDate) {
      ReactGA.event({
        category: 'Market Price',
        action: 'Custom Date Search',
        label: `${startDate} ~ ${endDate}`
      });
      fetchData(startDate, endDate);
    }
  };

  const processData = (data) => {
    let buyField, sellField;
    
    if (mode === 'gold') {
      switch (goldType) {
        case '24k':
          buyField = 'sell24k';
          sellField = 'buy24k';
          break;
        case '18k':
          buyField = 'sell18k';
          sellField = 'buy18k';
          break;
        case '14k':
          buyField = 'sell14k';
          sellField = 'buy14k';
          break;
        case 'white':
          buyField = 'sellWhite';
          sellField = 'buyWhite';
          break;
      }
    } else {
      buyField = 'sellSilver';
      sellField = 'buySilver';
    }
    
    setTimeout(() => {
      if (chartRef.current) {
        createChart([...data].reverse(), buyField, sellField);
      }
    }, 100);
    
    setTableData(data);
    setCurrentPage(1);
  };

  const fetchData = async (customStart, customEnd) => {
    try {
      setLoading(true);
      const end = new Date();
      const start = new Date();
      
      if (customStart && customEnd) {
        start.setTime(new Date(customStart).getTime());
        end.setTime(new Date(customEnd).getTime());
      } else {
        switch (period) {
          case 'month':
            start.setMonth(start.getMonth() - 1);
            break;
          case '3month':
            start.setMonth(start.getMonth() - 3);
            break;
          case '1year':
            start.setFullYear(start.getFullYear() - 1);
            break;
          case '3year':
            start.setFullYear(start.getFullYear() - 3);
            break;
          case '5year':
            start.setFullYear(start.getFullYear() - 5);
            break;
          default:
            start.setFullYear(start.getFullYear() - 10);
        }
      }
      
      const url = new URL('https://koreakookmingold.com/api/chart');
      url.searchParams.set('startDate', start.toISOString().split('T')[0]);
      url.searchParams.set('endDate', end.toISOString().split('T')[0]);
      
      const response = await fetch(url.toString());
      const result = await response.json();
      
      if (result?.history?.length) {
        setRawData(result.history);
        processData(result.history);
      }
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };



  const createChart = (data, buyField, sellField) => {
    if (!chartRef.current || !data.length) return;

    if (chartInstance.current) {
      try {
        chartInstance.current.destroy();
      } catch (e) {
        console.warn('Chart destroy error:', e);
      }
      chartInstance.current = null;
    }

    const width = window.innerWidth;
    const isMobile = width <= 576;
    const isTablet = width <= 768;
    
    const responsive = {
      fontSize: isMobile ? '8px' : isTablet ? '9px' : '11px',
      yFontSize: isMobile ? '9px' : isTablet ? '10px' : '11px',
      tickAmount: isMobile ? 5 : isTablet ? 7 : 10,
      legendSize: isMobile ? '10px' : isTablet ? '11px' : '12px',
      markerSize: isMobile ? 7 : 10,
      strokeWidth: isMobile ? 1.5 : 2,
      gridPadding: isMobile ? 0 : 5,
      yFormatter: (value) => isMobile ? (value / 1000).toFixed(0) + 'k' : value.toLocaleString()
    };

    const buyData = [];
    const sellData = [];
    const categories = [];
    
    const dataLength = data.length;
    const maxPoints = 365;
    const step = dataLength > maxPoints ? Math.ceil(dataLength / maxPoints) : 1;
    
    for (let i = 0; i < dataLength; i += step) {
      const item = data[i];
      buyData.push(item[buyField]);
      sellData.push(item[sellField]);
      const d = new Date(item.date);
      categories.push(`${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getDate().toString().padStart(2, '0')}`);
    }

    const options = {
      series: [
        { name: '살 때(3.75g)', data: buyData },
        { name: '팔 때(3.75g)', data: sellData }
      ],
      chart: {
        type: 'area',
        height: '100%',
        zoom: { enabled: false },
        toolbar: { show: false },
        animations: {
          enabled: dataLength < 500,
          easing: 'easeinout',
          speed: 800
        }
      },
      colors: ['#ef892d', '#8bca38'],
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: responsive.strokeWidth
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          hideOverlappingLabels: true,
          style: {
            colors: '#8e8da4',
            fontSize: responsive.fontSize
          }
        },
        tickAmount: responsive.tickAmount,
        tooltip: { enabled: false }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#8e8da4',
            fontSize: responsive.yFontSize
          },
          formatter: responsive.yFormatter,
          offsetX: isMobile ? -5 : 0
        }
      },
      grid: {
        borderColor: '#e0e0e0',
        padding: {
          left: responsive.gridPadding,
          right: responsive.gridPadding
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: responsive.legendSize,
        labels: { colors: '#373d3f' },
        markers: {
          width: responsive.markerSize,
          height: responsive.markerSize
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: { show: false },
        custom: function({ series, dataPointIndex }) {
          const actualIndex = Math.min(dataPointIndex * step, data.length - 1);
          const item = data[actualIndex];
          if (!item) return '';
          
          const d = new Date(item.date);
          const dateStr = `${d.getFullYear()}년 ${(d.getMonth() + 1).toString().padStart(2, '0')}월 ${d.getDate().toString().padStart(2, '0')}일`;
          
          let content = `<div style="padding: 8px 10px; background: #fff; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
              <div style="font-weight: 600; margin-bottom: 5px; color: #333; font-size: 11px; white-space: nowrap;">${dateStr}</div>`;
          
          if (series[0] && series[0][dataPointIndex] !== undefined) {
            content += `<div style="display: flex; align-items: center; margin-bottom: 3px;">
                <span style="width: 8px; height: 8px; background: #ef892d; border-radius: 50%; margin-right: 6px; flex-shrink: 0;"></span>
                <span style="color: #666; font-size: 10px; white-space: nowrap;">살 때: <strong style="color: #333;">${series[0][dataPointIndex].toLocaleString()}원</strong></span>
              </div>`;
          }
          
          if (series[1] && series[1][dataPointIndex] !== undefined) {
            content += `<div style="display: flex; align-items: center;">
                <span style="width: 8px; height: 8px; background: #8bca38; border-radius: 50%; margin-right: 6px; flex-shrink: 0;"></span>
                <span style="color: #666; font-size: 10px; white-space: nowrap;">팔 때: <strong style="color: #333;">${series[1][dataPointIndex].toLocaleString()}원</strong></span>
              </div>`;
          }
          
          content += `</div>`;
          return content;
        }
      }
    };

    try {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    } catch (e) {
      console.error('Chart render error:', e);
    }
  };

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  return (
    <>
      <SEO 
        title="금시세 조회 - 한국국민금거래소"
        description="한국국민금거래소의 실시간 금시세, 은시세를 확인하세요. 일별, 월별, 연별 금시세 추이 및 차트 제공"
        keywords="금시세, 은시세, 금값, 실시간금시세, 금시세표, 금시세추이, 순금시세, 18K시세, 14K시세, 한국국민금거래소"
        url="https://koreakookmingold.com/market-price"
      />
      <div className="market-price">
      <section className="market-price__hero">
        <div className="market-price__hero-content">
          <h1 className="market-price__hero-title">시세 조회</h1>
          <p className="market-price__hero-subtitle">
            한국국민금거래소와 함께<br />
            실시간 귀금속 시세를 확인하세요
          </p>
        </div>
      </section>

      <section className="market-price__section">
        <div className="market-price__container">
          <div className="market-price__section-header">
            <h2 className="market-price__section-title">한국국민금거래소 시세를 확인하세요</h2>
            <div className="market-price__section-divider"></div>
          </div>

          <div className="market-price__controls">
            <div className="market-price__control-card">
              <span className="market-price__control-label">금속 선택 (3.75g 기준)</span>
              <div className="market-price__tab-selector">
                <button 
                  className={mode === 'gold' ? 'active' : ''} 
                  onClick={() => setMode('gold')}
                >
                  금
                </button>
                <button 
                  className={mode === 'silver' ? 'active' : ''} 
                  onClick={() => setMode('silver')}
                >
                  은
                </button>
              </div>
            </div>

            <div className={`market-price__control-card ${mode === 'silver' ? 'market-price__control-card--hidden' : ''}`}>
              <span className="market-price__control-label">금 종류</span>
              <div className="market-price__gold-type-selector">
                <button 
                  className={goldType === '24k' ? 'active' : ''} 
                  onClick={() => setGoldType('24k')}
                >
                  24K
                </button>
                <button 
                  className={goldType === '18k' ? 'active' : ''} 
                  onClick={() => setGoldType('18k')}
                >
                  18K
                </button>
                <button 
                  className={goldType === '14k' ? 'active' : ''} 
                  onClick={() => setGoldType('14k')}
                >
                  14K
                </button>
                <button 
                  className={goldType === 'white' ? 'active' : ''} 
                  onClick={() => setGoldType('white')}
                >
                  백금
                </button>
              </div>
            </div>

            <div className="market-price__control-card">
              <span className="market-price__control-label">기간 선택</span>
              <div className="market-price__period-selector">
                <button 
                  className={period === 'month' ? 'active' : ''} 
                  onClick={() => setPeriod('month')}
                >
                  1개월
                </button>
                <button 
                  className={period === '3month' ? 'active' : ''} 
                  onClick={() => setPeriod('3month')}
                >
                  3개월
                </button>
                <button 
                  className={period === '1year' ? 'active' : ''} 
                  onClick={() => setPeriod('1year')}
                >
                  1년
                </button>
                <button 
                  className={period === '3year' ? 'active' : ''} 
                  onClick={() => setPeriod('3year')}
                >
                  3년
                </button>
                <button 
                  className={period === '5year' ? 'active' : ''} 
                  onClick={() => setPeriod('5year')}
                >
                  5년
                </button>
                <button 
                  className={period === 'all' ? 'active' : ''} 
                  onClick={() => setPeriod('all')}
                >
                  전체
                </button>
              </div>
            </div>

            <div className="market-price__control-card market-price__control-card--collapsible">
              <div 
                className="market-price__control-header"
                onClick={() => setIsDateSelectorOpen(!isDateSelectorOpen)}
              >
                <span className="market-price__control-label">직접 선택</span>
                <span className={`market-price__control-toggle ${isDateSelectorOpen ? 'market-price__control-toggle--open' : ''}`}>▼</span>
              </div>
              <div className={`market-price__date-selector ${isDateSelectorOpen ? 'market-price__date-selector--open' : ''}`}>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span>~</span>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <button onClick={handleCustomDateSearch}>조회</button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="market-price__loading">데이터를 불러오는 중...</div>
          ) : (
            <>
              <div className="market-price__chart-container">
                <div ref={chartRef} />
              </div>

              <div className="market-price__table-container">
                <div className="table-header">
                  <h3 className="market-price__table-title">최근 시세 내역</h3>
                  {mode === 'gold' && (
                    <div className="table-display-options">
                      <button 
                        className={displayOption === 'buy' ? 'active' : ''}
                        onClick={() => setDisplayOption('buy')}
                      >
                        살 때
                      </button>
                      <button 
                        className={displayOption === 'sell' ? 'active' : ''}
                        onClick={() => setDisplayOption('sell')}
                      >
                        팔 때
                      </button>
                    </div>
                  )}
                </div>
            <div className="price-table-wrapper">
            <table className="price-table">
              <thead>
                <tr>
                  <th>날짜</th>
                  {mode === 'gold' ? (
                    <>
                      <th className={displayOption === 'buy' ? 'buy-header' : 'sell-header'}>24K</th>
                      <th className={displayOption === 'buy' ? 'buy-header' : 'sell-header'}>18K</th>
                      <th className={displayOption === 'buy' ? 'buy-header' : 'sell-header'}>14K</th>
                      <th className={displayOption === 'buy' ? 'buy-header' : 'sell-header'}>백금</th>
                    </>
                  ) : (
                    <>
                      <th className="buy-header">은 살 때</th>
                      <th className="sell-header">은 팔 때</th>
                    </>
                  )}
                </tr>
                <tr className="unit-row">
                  <th></th>
                  {mode === 'gold' ? (
                    <>
                      <th colSpan="4">(3.75g 기준)</th>
                    </>
                  ) : (
                    <>
                      <th>(3.75g 기준)</th>
                      <th>(3.75g 기준)</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((item, index) => {
                  const actualIndex = (currentPage - 1) * itemsPerPage + index;
                  const prevItem = tableData[actualIndex + 1];
                  
                  return (
                    <tr key={index}>
                      <td>{item.date}</td>
                      {mode === 'gold' ? (
                        <>
                          <td className={displayOption === 'buy' ? 'buy-cell' : 'sell-cell'}>
                            {displayOption === 'buy' ? renderCell(item.sell24k, prevItem?.sell24k) : renderCell(item.buy24k, prevItem?.buy24k)}
                          </td>
                          <td className={displayOption === 'buy' ? 'buy-cell' : 'sell-cell'}>
                            {displayOption === 'buy' ? renderCell(item.sell18k, prevItem?.sell18k) : renderCell(item.buy18k, prevItem?.buy18k)}
                          </td>
                          <td className={displayOption === 'buy' ? 'buy-cell' : 'sell-cell'}>
                            {displayOption === 'buy' ? renderCell(item.sell14k, prevItem?.sell14k) : renderCell(item.buy14k, prevItem?.buy14k)}
                          </td>
                          <td className={displayOption === 'buy' ? 'buy-cell' : 'sell-cell'}>
                            {displayOption === 'buy' ? renderCell(item.sellWhite, prevItem?.sellWhite) : renderCell(item.buyWhite, prevItem?.buyWhite)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="buy-cell">
                            {renderCell(item.sellSilver, prevItem?.sellSilver)}
                          </td>
                          <td className="sell-cell">
                            {renderCell(item.buySilver, prevItem?.buySilver)}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            
            {tableData.length > itemsPerPage && (
              <div className="market-price__pagination">
                <button 
                  onClick={() => {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                    ReactGA.event({
                      category: 'Market Price',
                      action: 'Pagination',
                      label: '이전'
                    });
                  }}
                  disabled={currentPage === 1}
                >
                  이전
                </button>
                <span>{currentPage} / {Math.ceil(tableData.length / itemsPerPage)}</span>
                <button 
                  onClick={() => {
                    setCurrentPage(prev => Math.min(Math.ceil(tableData.length / itemsPerPage), prev + 1));
                    ReactGA.event({
                      category: 'Market Price',
                      action: 'Pagination',
                      label: '다음'
                    });
                  }}
                  disabled={currentPage === Math.ceil(tableData.length / itemsPerPage)}
                >
                  다음
                </button>
              </div>
            )}
          </div>
        </>
      )}
        </div>
      </section>
    </div>
    </>
  );
}

export default MarketPrice;
